import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  HStack,
  Icon,
  Popover,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import {
  LifeCycleStage,
  tLifeCycleStage,
} from "../../../../../osaway/models/LifeCycleStage";
import { LifeCycleStageTag } from "../../../../../osaway/views/LifeCycleStageTag/LifeCycleStageTag";
import { OsaWayApi } from "../../../../../osaway/services/OsaWayApi";
import { useStores } from "../../../../../shared/stores/RootStore";
import { useState } from "react";
import {
  ArrowBackIcon,
  ArrowForwardIcon,
} from "../../../../../shared/views/icons";
import { CustomPopover } from "../../../../../shared/views/CustomPopover";
import { OsaWay } from "../../../../../osaway/models/OsaWay";

const LifeCycleStageControlsBase: React.FC<{
  osaWay: OsaWay;
  size: "sm" | "md" | "lg" | "xl" | "xs" | undefined;
}> = ({ osaWay, size }) => {
  const { osaWayStore, osaNodeStore } = useStores();
  const [loading, setLoading] = useState(false);
  const goToNextPopover = useDisclosure();

  const nextStage = getNextStage(osaWay.lifeCycleStage);
  const previousStage = getPreviousStage(osaWay.lifeCycleStage);

  const goToPrevious = async () => {
    await setLifeCycleStage(previousStage);
  };

  const goToNext = async () => {
    goToNextPopover.onClose();
    await setLifeCycleStage(nextStage);
    if (nextStage === LifeCycleStage.Review) {
      await OsaWayApi.analyzeCoding(osaWay.id);
      await osaNodeStore.fetchNodes(osaWay.id);
    }
  };

  const setLifeCycleStage = async (lifeCycleStage: LifeCycleStage) => {
    setLoading(true);
    await osaWayStore.updateLifeCycleStage(osaWay.id, lifeCycleStage);
    setLoading(false);
  };

  return (
    <HStack px={2} alignSelf="center" gap={0}>
      {!isFirst(osaWay.lifeCycleStage) && (
        <Button
          size={size}
          variant="ghost"
          disabled={!osaWay.permissions.canRevertLifeCycleStage}
          loading={loading}
          onClick={goToPrevious}
          title={tLifeCycleStage(previousStage)}
        >
          <Icon size={size} as={ArrowBackIcon} />
        </Button>
      )}

      <LifeCycleStageTag fontSize={size}>
        {tLifeCycleStage(osaWay.lifeCycleStage)}
      </LifeCycleStageTag>

      {!isLast(osaWay.lifeCycleStage) && (
        <CustomPopover
          w={400}
          open={goToNextPopover.open}
          onClose={goToNextPopover.onClose}
          trigger={
            <Button
              onClick={goToNextPopover.onOpen}
              size={size}
              fontSize={size}
              variant="ghost"
              loading={loading}
              disabled={!osaWay.permissions.canUpdateLifeCycleStage}
            >
              <Icon size={size} as={ArrowForwardIcon} />{" "}
              {tLifeCycleStage(nextStage)}
            </Button>
          }
        >
          <GoToNextPopover
            osaWay={osaWay}
            loading={loading}
            goToNext={goToNext}
          />
        </CustomPopover>
      )}
    </HStack>
  );
};

const GoToNextPopover: React.FC<{
  loading: boolean;
  osaWay: OsaWay;
  goToNext: () => Promise<void>;
}> = ({ osaWay, loading, goToNext }) => {
  const stats = osaWay.stats!;

  const nextStage = getNextStage(osaWay.lifeCycleStage);

  const checkError = () => {
    if (nextStage === LifeCycleStage.Coding && !osaWay.uploadFinished) {
      return t("home:route.lifeCycle.uploadNotFinished");
    } else if (
      nextStage === LifeCycleStage.Review &&
      stats.nNodesCoded < stats.nNodes
    ) {
      return t("home:route.lifeCycle.routeNotCoded");
    } else if (nextStage === LifeCycleStage.Coding && stats.nNodes === 0) {
      return t("home:route.lifeCycle.noNodes");
    }
    return;
  };

  const error = checkError();

  return (
    <>
      <Popover.Header>
        <Text fontWeight="semibold">
          {t("home:route.lifeCycle.goTo", {
            stage: tLifeCycleStage(nextStage),
          })}
        </Text>
      </Popover.Header>
      <Popover.Body fontSize={"sm"}>
        <Stack>
          {!!error ? (
            <Alert.Root status="error">
              <Alert.Indicator />
              <Alert.Content>
                <AlertTitle>{t("home:route.lifeCycle.notReady")}</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert.Content>
            </Alert.Root>
          ) : (
            <Alert.Root status="warning">
              <Alert.Indicator />
              <Alert.Content>
                <AlertDescription>
                  {t(`home:route.lifeCycle.confirmNext.${nextStage}`)}
                </AlertDescription>
              </Alert.Content>
            </Alert.Root>
          )}
          <HStack justifyContent={"end"}>
            <Button
              size={"sm"}
              colorPalette="primary"
              disabled={!!error}
              loading={loading}
              onClick={goToNext}
            >
              {t(`shared:next`)}
            </Button>
          </HStack>
        </Stack>
      </Popover.Body>
    </>
  );
};

export const LifeCycleStageControls = observer(LifeCycleStageControlsBase);

const stageOrder = Object.values(LifeCycleStage);

const getNextStage = (current: LifeCycleStage) => {
  const currentIndex = stageOrder.indexOf(current);
  return stageOrder[currentIndex + 1];
};

const getPreviousStage = (current: LifeCycleStage) => {
  const currentIndex = stageOrder.indexOf(current);
  return stageOrder[currentIndex - 1];
};

const isFirst = (stage: LifeCycleStage) => stage === stageOrder[0];
const isLast = (stage: LifeCycleStage) =>
  stage === stageOrder[stageOrder.length - 1];